import { __assign, __extends } from "tslib";
import React from 'react';
import { SplitComponent } from './SplitClient';
import { WARN_SF_CONFIG_AND_FACTORY, ERROR_SF_NO_CONFIG_AND_FACTORY } from './constants';
import { getSplitFactory, destroySplitFactory, getSplitClient } from './utils';
import { DEFAULT_UPDATE_OPTIONS } from './useSplitClient';
/**
 * SplitFactory will initialize the Split SDK and its main client, listen for its events in order to update the Split Context,
 * and automatically shutdown and release resources when it is unmounted. SplitFactory must wrap other components and functions
 * from this library, since they access the Split Context and its properties (factory, client, isReady, etc).
 *
 * The underlying SDK factory and client is set on the constructor, and cannot be changed during the component lifecycle,
 * even if the component is updated with a different config or factory prop.
 *
 * @deprecated Replace with the new `SplitFactoryProvider` component.
 * `SplitFactoryProvider` is a drop-in replacement that properly handles side effects (factory creation and destruction) within the React component lifecycle, avoiding issues with factory recreation and memory leaks.
 * Note: There is a subtle breaking change in `SplitFactoryProvider`. When using the `config` prop, `factory` and `client` properties in the context are `null` in the first render, until the context is updated when some event is emitted on
 * the SDK main client (ready, ready from cache, timeout or update depending on the configuration of the `updateOnXXX` props of the component). This differs from the previous behavior where `factory` and `client` were immediately available.
 *
 * @see {@link https://help.split.io/hc/en-us/articles/360038825091-React-SDK#2-instantiate-the-sdk-and-create-a-new-split-client}
 */
var SplitFactory = /** @class */ (function (_super) {
    __extends(SplitFactory, _super);
    function SplitFactory(props) {
        var _this = _super.call(this, props) || this;
        // Log warning and error
        var propFactory = props.factory, config = props.config;
        if (!config && !propFactory) {
            console.error(ERROR_SF_NO_CONFIG_AND_FACTORY);
        }
        if (config && propFactory) {
            console.log(WARN_SF_CONFIG_AND_FACTORY);
        }
        // Instantiate factory
        var factory = null;
        if (propFactory) {
            factory = propFactory;
        }
        else {
            if (config) {
                // We use an idempotent variant of the Split factory builder (i.e., given the same config, it returns the same already
                // created instance), since React component constructors is part of render-phase and can be invoked multiple times.
                factory = getSplitFactory(config);
            }
        }
        _this.isFactoryExternal = propFactory ? true : false;
        // Instantiate main client. Attributes are set on `SplitComponent.getDerivedStateFromProps`
        var client = factory ? getSplitClient(factory) : null;
        _this.state = {
            client: client,
            factory: factory,
        };
        return _this;
    }
    SplitFactory.prototype.componentWillUnmount = function () {
        // only destroy the client if the factory was created internally. Otherwise, the shutdown must be handled by the user
        if (!this.isFactoryExternal && this.state.factory) {
            destroySplitFactory(this.state.factory);
        }
    };
    SplitFactory.prototype.render = function () {
        var _a = this.state, factory = _a.factory, client = _a.client;
        return (React.createElement(SplitComponent, __assign({}, this.props, { factory: factory, client: client })));
    };
    SplitFactory.defaultProps = __assign({ children: null }, DEFAULT_UPDATE_OPTIONS);
    return SplitFactory;
}(React.Component));
export { SplitFactory };
